import { FormField, FormTextArea, FormSelect, FormCheckbox } from '../shared/form';
import type { SchemaProperty } from '../../utils/schemaUtils';
import { getFieldType } from '../../utils/schemaUtils';

interface SchemaFieldRendererProps {
  property: SchemaProperty;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  error?: string;
}

export function SchemaFieldRenderer({
  property,
  value,
  onChange,
  error,
}: SchemaFieldRendererProps) {
  const fieldType = getFieldType(property);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    let newValue: unknown = e.target.value;

    // Convert to appropriate type
    if (fieldType === 'number' && e.target.value !== '') {
      newValue = property.type === 'integer'
        ? parseInt(e.target.value, 10)
        : parseFloat(e.target.value);
    }

    onChange(property.name, newValue);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(property.name, e.target.checked);
  };

  const label = property.description || formatLabel(property.name);

  // Render based on field type
  switch (fieldType) {
    case 'checkbox':
      return (
        <FormCheckbox
          label={label}
          name={property.name}
          checked={Boolean(value)}
          onChange={handleCheckboxChange}
          error={error}
          description={property.description ? undefined : formatLabel(property.name)}
        />
      );

    case 'select':
      return (
        <FormSelect
          label={label}
          name={property.name}
          value={String(value ?? '')}
          onChange={handleChange}
          options={
            property.enum?.map(v => ({ value: v, label: v })) || []
          }
          error={error}
          required={property.required}
          placeholder="Select an option..."
        />
      );

    case 'textarea':
      return (
        <FormTextArea
          label={label}
          name={property.name}
          value={String(value ?? '')}
          onChange={handleChange}
          error={error}
          required={property.required}
          placeholder={property.description || `Enter ${formatLabel(property.name).toLowerCase()}`}
          rows={4}
        />
      );

    case 'number':
      return (
        <FormField
          label={label}
          name={property.name}
          type="number"
          value={value === undefined || value === null ? '' : Number(value)}
          onChange={handleChange}
          error={error}
          required={property.required}
          placeholder={getNumberPlaceholder(property)}
          min={property.minimum}
          max={property.maximum}
          step={property.type === 'integer' ? 1 : 'any'}
        />
      );

    case 'email':
      return (
        <FormField
          label={label}
          name={property.name}
          type="email"
          value={String(value ?? '')}
          onChange={handleChange}
          error={error}
          required={property.required}
          placeholder="email@example.com"
        />
      );

    case 'url':
      return (
        <FormField
          label={label}
          name={property.name}
          type="url"
          value={String(value ?? '')}
          onChange={handleChange}
          error={error}
          required={property.required}
          placeholder="https://example.com"
        />
      );

    case 'date':
      return (
        <FormField
          label={label}
          name={property.name}
          type="date"
          value={String(value ?? '')}
          onChange={handleChange}
          error={error}
          required={property.required}
        />
      );

    case 'datetime':
      return (
        <FormField
          label={label}
          name={property.name}
          type="datetime-local"
          value={String(value ?? '')}
          onChange={handleChange}
          error={error}
          required={property.required}
        />
      );

    case 'json':
      // For complex types, render as JSON textarea
      return (
        <FormTextArea
          label={label}
          name={property.name}
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '')}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              onChange(property.name, parsed);
            } catch {
              // Keep as string if not valid JSON
              onChange(property.name, e.target.value);
            }
          }}
          error={error}
          required={property.required}
          placeholder={`Enter ${property.type} as JSON`}
          rows={4}
        />
      );

    case 'text':
    default:
      return (
        <FormField
          label={label}
          name={property.name}
          type="text"
          value={String(value ?? '')}
          onChange={handleChange}
          error={error}
          required={property.required}
          placeholder={property.description || `Enter ${formatLabel(property.name).toLowerCase()}`}
        />
      );
  }
}

/**
 * Format a camelCase or snake_case field name as a readable label
 */
function formatLabel(name: string): string {
  return name
    // Insert space before capital letters
    .replace(/([A-Z])/g, ' $1')
    // Replace underscores with spaces
    .replace(/_/g, ' ')
    // Capitalize first letter
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

/**
 * Get placeholder text for number fields
 */
function getNumberPlaceholder(property: SchemaProperty): string {
  const parts: string[] = [];

  if (property.minimum !== undefined && property.maximum !== undefined) {
    parts.push(`${property.minimum}-${property.maximum}`);
  } else if (property.minimum !== undefined) {
    parts.push(`min: ${property.minimum}`);
  } else if (property.maximum !== undefined) {
    parts.push(`max: ${property.maximum}`);
  }

  if (parts.length > 0) {
    return parts.join(', ');
  }

  return property.type === 'integer' ? 'Enter integer' : 'Enter number';
}
